export const EMPTY_LIBRARY = { folders: [], placements: {} };
export const FOLDER_COLORS = [
    '#6554d9',
    '#168b89',
    '#dc7a2e',
    '#d55780',
    '#3f78c9',
    '#687181',
];
function record(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
export function parseLibrary(value) {
    if (!record(value) || !Array.isArray(value.folders) || !record(value.placements))
        return EMPTY_LIBRARY;
    const folders = [];
    for (const item of value.folders) {
        if (!record(item) ||
            typeof item.id !== 'string' ||
            typeof item.name !== 'string' ||
            !(item.parentId === null || typeof item.parentId === 'string') ||
            folders.some((f) => f.id === item.id))
            continue;
        folders.push({
            id: item.id,
            name: item.name.slice(0, 100),
            parentId: item.parentId,
            color: typeof item.color === 'string' && /^#[a-f0-9]{6}$/i.test(item.color)
                ? item.color
                : FOLDER_COLORS[0],
            trashed: item.trashed === true,
        });
    }
    const placements = Object.create(null);
    for (const [id, item] of Object.entries(value.placements)) {
        if (!record(item))
            continue;
        placements[id] = {
            folderId: typeof item.folderId === 'string' && folders.some((f) => f.id === item.folderId)
                ? item.folderId
                : null,
            tags: Array.isArray(item.tags)
                ? item.tags
                    .filter((tag) => typeof tag === 'string')
                    .slice(0, 20)
                    .map((tag) => tag.slice(0, 40))
                : [],
            ...(item.favorite === true ? { favorite: true } : {}),
        };
    }
    return { folders, placements };
}
export function folderPath(library, id) {
    const path = [];
    const seen = new Set();
    let current = id;
    while (current !== null && !seen.has(current)) {
        seen.add(current);
        const folder = library.folders.find((f) => f.id === current);
        if (folder === undefined)
            break;
        path.unshift(folder);
        current = folder.parentId;
    }
    return path;
}
export function folderInTrash(library, id) {
    return folderPath(library, id).some((folder) => folder.trashed);
}
export function saveFolder(library, folder) {
    if (!folder.name.trim())
        throw new Error('Informe um nome para a pasta.');
    if (folder.parentId !== null &&
        (!library.folders.some((f) => f.id === folder.parentId) ||
            folderPath(library, folder.parentId).some((f) => f.id === folder.id)))
        throw new Error('A pasta não pode ser movida para dentro dela mesma.');
    return {
        ...library,
        folders: [
            ...library.folders.filter((f) => f.id !== folder.id),
            { ...folder, name: folder.name.trim().slice(0, 100) },
        ],
    };
}
export function placeMap(library, mapId, folderId, tags) {
    if (folderId !== null &&
        (!library.folders.some((f) => f.id === folderId) || folderInTrash(library, folderId)))
        throw new Error('Escolha uma pasta disponível.');
    return {
        ...library,
        placements: {
            ...library.placements,
            [mapId]: {
                folderId,
                tags: [
                    ...new Set(tags
                        .split(',')
                        .map((tag) => tag.trim().slice(0, 40))
                        .filter(Boolean)),
                ].slice(0, 20),
                // Moving or retagging a map keeps it pinned: those are different decisions.
                ...(library.placements[mapId]?.favorite === true ? { favorite: true } : {}),
            },
        },
    };
}
/** Pins a map to the favourites, or unpins it. Its folder and tags are left as they were. */
export function setFavorite(library, mapId, favorite) {
    const current = library.placements[mapId] ?? { folderId: null, tags: [] };
    const { favorite: _previous, ...rest } = current;
    return {
        ...library,
        placements: {
            ...library.placements,
            [mapId]: favorite ? { ...rest, favorite: true } : rest,
        },
    };
}
export function isFavorite(library, mapId) {
    return library.placements[mapId]?.favorite === true;
}
/** Whether two organizations say the same thing, whatever order their keys were written in. */
export function sameLibrary(a, b) {
    return canonical(a) === canonical(b);
}
function canonical(library) {
    const folders = [...library.folders]
        .sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0))
        .map((f) => [f.id, f.name, f.parentId, f.color, f.trashed]);
    const placements = Object.keys(library.placements)
        .sort()
        .map((id) => {
        const p = library.placements[id];
        return [id, p?.folderId ?? null, p?.tags ?? [], p?.favorite === true];
    });
    return JSON.stringify([folders, placements]);
}
function sameEntry(a, b) {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
/**
 * Three-way merge of a library organization: what both sides last agreed on (`base`), what this
 * device holds now (`local`) and what the account holds now (`remote`).
 *
 * Entry by entry — a folder by its id, a map's placement by the map's id: a side that did not
 * change an entry since `base` takes the other side's version; when both changed the same entry,
 * this device wins, because it is the change the person is looking at. An entry one side removed
 * and the other left alone is removed. Folders are never lost to a conflict: at worst one of two
 * renames wins.
 *
 * The result is then checked the way a stored library is (`parseLibrary`), so a placement whose
 * folder no longer exists falls back to the top level instead of pointing at nothing.
 */
export function mergeLibraries(base, local, remote) {
    const pick = (b, l, r) => {
        if (sameEntry(l, b))
            return r;
        if (sameEntry(r, b))
            return l;
        return l ?? r;
    };
    const byId = (list) => new Map(list.map((folder) => [folder.id, folder]));
    const baseFolders = byId(base.folders);
    const localFolders = byId(local.folders);
    const remoteFolders = byId(remote.folders);
    // Local order first, then what only the account has, so a merge never reshuffles this device.
    const folderIds = [
        ...local.folders.map((folder) => folder.id),
        ...remote.folders.map((folder) => folder.id).filter((id) => !localFolders.has(id)),
    ];
    const folders = [];
    for (const id of folderIds) {
        const merged = pick(baseFolders.get(id), localFolders.get(id), remoteFolders.get(id));
        if (merged !== undefined)
            folders.push(merged);
    }
    const placements = {};
    const mapIds = new Set([...Object.keys(local.placements), ...Object.keys(remote.placements)]);
    for (const id of mapIds) {
        const merged = pick(base.placements[id], local.placements[id], remote.placements[id]);
        if (merged !== undefined)
            placements[id] = merged;
    }
    return parseLibrary({ folders, placements });
}
//# sourceMappingURL=library.js.map